import { FC, ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ArrowIcon } from '../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { FadeIn } from '../../../components/motion/index';
import {
  Company,
  EventHooks,
  useFormat,
  useLocale,
  useModalState,
  useNavigate,
} from '../../../hooks/index';
import { eventFormType, EventTypeBackend, EventUnion } from '../../../types/events.types';
import { PageRoute } from '../../../types/page.types';
import {
  downloadCompanyStorageFilesSequentially,
  openCompanyStorageUrlInNewTab,
} from '../../../utils/company-storage-url.util';
import { EventsTransactionDetailPlaceholder } from '../components/events-loading-placeholders';
import { FilesModal } from '../modals/index';
import { Tag, TagVariant } from '../tag';
import { getEventTransactionDate } from '../utils';
import { getPoolAdjustmentTitle, isPoolAdjustmentEvent } from '../utils/pool-event-row.util';
import { resolveEventLevelDocumentLinks } from '../utils/resolve-event-document-links.util';
import { Approval } from './transaction-card-variants/approval';
import { Buyback } from './transaction-card-variants/buyback';
import { CompanyIncorporation } from './transaction-card-variants/company-incorporation';
import { Conversion } from './transaction-card-variants/conversion';
import { Exercised } from './transaction-card-variants/exercise';
import { Expire } from './transaction-card-variants/expire';
import { FundraisingRound } from './transaction-card-variants/fundraising-round';
import { Grant } from './transaction-card-variants/grant';
import { NewPlan } from './transaction-card-variants/new-plan';
import { Payout } from './transaction-card-variants/payout';
import { PoolEvent } from './transaction-card-variants/pool-event';
import { Safe } from './transaction-card-variants/safe';
import { SafeConversion } from './transaction-card-variants/safe-conversion';
import { Secondaries } from './transaction-card-variants/secondaries';
import { ShareIssuance } from './transaction-card-variants/share-issuance';
import { CardProps } from './transaction-card-variants/transaction-card-variants.types';
import { Valuation } from './transaction-card-variants/valuation';

const transactionCards: Record<EventTypeBackend, (props: CardProps) => ReactNode> = {
  [EventTypeBackend.CONVERSION]: (props) => <Conversion {...props} />,
  [EventTypeBackend.GRANT]: (props) => <Grant {...props} />,
  [EventTypeBackend.BUYBACK]: (props) => <Buyback {...props} />,
  [EventTypeBackend.FUNDRAISING_ROUND]: (props) => <FundraisingRound {...props} />,
  [EventTypeBackend.NEW_PLAN]: (props) => <NewPlan {...props} />,
  [EventTypeBackend.NEW_POOL]: (props) => <PoolEvent {...props} />,
  [EventTypeBackend.POOL_INCREASE]: (props) => <PoolEvent {...props} />,
  [EventTypeBackend.POOL_DECREASE]: (props) => <PoolEvent {...props} />,
  [EventTypeBackend.SHARE_ISSUANCE]: (props) => <ShareIssuance {...props} />,
  [EventTypeBackend.SECONDARIES]: (props) => <Secondaries {...props} />,
  [EventTypeBackend.VALUATION]: (props) => <Valuation {...props} />,
  [EventTypeBackend.EXERCISE]: (props) => <Exercised {...props} />,
  [EventTypeBackend.APPROVAL]: (props) => <Approval {...props} />,
  [EventTypeBackend.PAYOUT]: (props) => <Payout {...props} />,
  [EventTypeBackend.EXPIRE]: (props) => <Expire {...props} />,
  [EventTypeBackend.SAFE_CONVERSION]: (props) => <SafeConversion {...props} />,
  [EventTypeBackend.SAFE]: (props) => <Safe {...props} />,
  [EventTypeBackend.COMPANY_INCORPORATION]: (props) => <CompanyIncorporation {...props} />,
};

export const TransactionsList: FC = () => {
  const navigate = useNavigate(PageRoute.EVENTS);
  const { id } = useParams();
  const [fileLinksStore, setFileLinks] = useState<string[]>([]);
  const { format } = useFormat();
  const { selectedCompanyId } = Company.useSelected();
  const { event, isLoading } = EventHooks.useEvent({
    companyId: selectedCompanyId,
    eventId: id || '',
  });
  const { messagesLocale } = useLocale();
  const mergedEventUnion = event as MergeUnion<EventUnion> | undefined;

  const originalName = mergedEventUnion?.valuation?.name || mergedEventUnion?.name;
  const transactionDate = event ? getEventTransactionDate(event) : new Date();

  // Same title as the history list: an adjustment has no name of its own and carries its pool's.
  const poolAdjustmentTitle =
    event && isPoolAdjustmentEvent(event)
      ? getPoolAdjustmentTitle(event, messagesLocale[eventFormType[event.type]])
      : undefined;

  const name =
    poolAdjustmentTitle ||
    originalName ||
    (mergedEventUnion?.type && messagesLocale[eventFormType?.[mergedEventUnion?.type]]);

  const handleDocumentClick = (link: string) => {
    if (!selectedCompanyId) return;
    void openCompanyStorageUrlInNewTab(selectedCompanyId, link);
  };

  const handleDownloadAllDocuments = () => {
    if (!selectedCompanyId || fileLinksStore.length === 0) return;
    void downloadCompanyStorageFilesSequentially(selectedCompanyId, fileLinksStore);
  };

  const {
    isOpen: isOpenFilesModal,
    handleCloseModal: handleCloseFilesModal,
    handleSuccessModal: handleSuccessFilesModal,
    handleOpenModal: handleOpenFilesModal,
    toggler: filesModalToggler,
  } = useModalState<never, never, string[]>({
    onSuccess: () => {
      handleDownloadAllDocuments();
      setFileLinks([]);
    },
    onOpen: (fileLinks) => fileLinks && setFileLinks(fileLinks),
    onClose: () => setFileLinks([]),
  });

  if (!event || isLoading) return <EventsTransactionDetailPlaceholder />;
  const eventUnion = event as MergeUnion<EventUnion>;
  const { allLinks: eventDocumentFileLinks, docusignLinks: eventDocusignFileLinks } =
    resolveEventLevelDocumentLinks(eventUnion);
  const title = poolAdjustmentTitle || eventUnion.name || (
    <AppFormattedMessage id={eventFormType[eventUnion.type]} />
  );

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: filesModalToggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={event?.createdAt}
          docusignFileLinks={eventDocusignFileLinks}
          filesLinks={eventDocumentFileLinks}
          name={name || ''}
          onClose={handleCloseFilesModal}
          onDownloadFile={handleDocumentClick}
          onSuccess={handleSuccessFilesModal}
        />
      </AlertDialogWrapper>
      <FadeIn className="w-full p-[1px]">
        <div className="flex gap-1 py-4">
          <span
            className="cursor-pointer text-xs font-[450] text-brand-700"
            onClick={() => navigate()}
          >
            <AppFormattedMessage id="EVENT_HISTORY" />
          </span>
          <span className="text-xs font-[450] text-gray-400">/ {title}</span>
        </div>
        <div className="flex items-center justify-between border-y border-gray-100 py-3">
          <div className="flex items-center gap-2">
            <Button
              className="centered size-9 rounded-full"
              onClick={() => navigate()}
              style={{
                boxShadow: '0px 0px 24px 0px #00000014',
                border: '1px solid #F2F2F2',
              }}
              styleType="NONE"
            >
              <ArrowIcon />
            </Button>
            <div className="flex flex-col">
              <span className="text-base font-semibold text-gray-700">{title}</span>
              <span className="text-label-sm font-medium text-gray-500">
                {format(transactionDate, 'dd/MM/yyyy')}
              </span>
            </div>
          </div>
          {event.type === EventTypeBackend.FUNDRAISING_ROUND && event.isOpen && (
            <Tag title={<AppFormattedMessage id="OPEN_ROUND" />} variant={TagVariant.GREEN} />
          )}
          {event.type === EventTypeBackend.FUNDRAISING_ROUND && !event.isOpen && (
            <Tag title={<AppFormattedMessage id="CLOSED_ROUND" />} variant={TagVariant.RED} />
          )}
        </div>
        <div className="flex flex-col gap-4 pb-1 pt-6">
          {transactionCards[event.type]({
            event,
            companyId: selectedCompanyId,
            openFileUploadModal: handleOpenFilesModal,
          })}
        </div>
      </FadeIn>
    </>
  );
};
